import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { formatDate } from '@/utils/format';

const { Text } = Typography;

function calendarDays(from: dayjs.Dayjs, to: dayjs.Dayjs): number {
  return to.startOf('day').diff(from.startOf('day'), 'day') + 1;
}

const EssLeavePage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState<string>();
  const dates = Form.useWatch('dates', form) as [dayjs.Dayjs, dayjs.Dayjs] | undefined;
  const days = dates?.[0] && dates?.[1] ? calendarDays(dates[0], dates[1]) : 1;

  const { data: context, isLoading: ctxLoading } = useQuery({
    queryKey: [...hrmsKeys.essLeaveApplyContext(), days],
    queryFn: () => hrmsService.getEssLeaveApplyContext({ days }),
  });

  const { data: apps = [], isLoading: appsLoading } = useQuery({
    queryKey: hrmsKeys.essLeaveApplications(),
    queryFn: () => hrmsService.listEssLeaveApplications(),
  });

  const balances = context?.balances || [];
  const leaveTypes = context?.leave_types || [];
  const authority = context?.approval_authority;
  const manager = context?.reporting_manager;

  const leaveTypeOptions = useMemo(
    () =>
      leaveTypes.map((t: any) => ({
        value: t.leave_code || t.leave_type,
        label: t.leave_name || t.leave_code,
      })),
    [leaveTypes]
  );

  const selectedBalance = useMemo(
    () =>
      balances.find(
        (b: any) =>
          String(b.leave_type || '').toUpperCase() === String(selectedType || '').toUpperCase()
      ),
    [balances, selectedType]
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: hrmsKeys.essLeaveApplications() });
    queryClient.invalidateQueries({ queryKey: hrmsKeys.essLeaveBalances() });
    queryClient.invalidateQueries({ queryKey: hrmsKeys.essLeaveApplyContext() });
    queryClient.invalidateQueries({ queryKey: hrmsKeys.essSummary() });
  };

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => hrmsService.createEssLeaveApplication(body),
    onSuccess: (_data, vars) => {
      message.success(vars.submit ? 'Leave submitted for approval' : 'Leave saved as draft');
      form.resetFields();
      setSelectedType(undefined);
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Leave save failed'),
  });

  const submitMut = useMutation({
    mutationFn: (id: string) => hrmsService.submitEssLeaveApplication(id),
    onSuccess: () => {
      message.success('Leave submitted for approval');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const save = async (submit: boolean) => {
    const v = await form.validateFields();
    createMut.mutate({
      leave_type: v.leave_type,
      from_date: v.dates[0].format('YYYY-MM-DD'),
      to_date: v.dates[1].format('YYYY-MM-DD'),
      reason: v.reason,
      duty_resume_date: v.duty_resume_date ? v.duty_resume_date.format('YYYY-MM-DD') : undefined,
      submit,
    });
  };

  const balanceCols: ColumnsType<any> = [
    { title: 'Year', dataIndex: 'leave_year', width: 80 },
    {
      title: 'Leave type',
      dataIndex: 'leave_type_name',
      render: (v, r) => v || r.leave_name || r.leave_type,
    },
    { title: 'Opening', dataIndex: 'opening_balance', width: 90 },
    { title: 'Accrued', dataIndex: 'accrued', width: 90 },
    { title: 'Taken', dataIndex: 'taken_days', width: 80 },
    { title: 'Pending', dataIndex: 'pending', width: 90, render: (v) => Number(v || 0) },
    {
      title: 'Available',
      dataIndex: 'available_days',
      width: 100,
      render: (v, r) => (
        <Text strong>{v ?? r.balance_days ?? '—'}</Text>
      ),
    },
  ];

  const appCols: ColumnsType<any> = [
    {
      title: 'Number',
      dataIndex: 'leave_application_number',
      width: 140,
      render: (v) => v || '—',
    },
    {
      title: 'From',
      dataIndex: 'from_date',
      width: 120,
      render: (d: string) => (d ? formatDate(d) : '—'),
    },
    {
      title: 'To',
      dataIndex: 'to_date',
      width: 120,
      render: (d: string) => (d ? formatDate(d) : '—'),
    },
    { title: 'Type', dataIndex: 'leave_type', width: 110 },
    { title: 'Days', dataIndex: 'total_days', width: 70 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Approval authority',
      key: 'authority',
      ellipsis: true,
      render: (_, r) => {
        const step = r.approval?.current_step;
        if (step?.assignee_name || step?.level_name) {
          return [step.level_name, step.assignee_name].filter(Boolean).join(' · ');
        }
        if (String(r.status) === 'Draft') return 'Not submitted';
        return '—';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, r) =>
        String(r.status) === 'Draft' || String(r.status) === 'Rejected' ? (
          <Button
            size="small"
            type="link"
            loading={submitMut.isPending}
            onClick={() => submitMut.mutate(r.id)}
          >
            Submit for approval
          </Button>
        ) : null,
    },
  ];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="My leave"
          subtitle="Balances, apply (draft or submit), and approval authority"
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Leave is saved as Draft unless you choose Submit for approval. Drafts do not notify approvers."
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {balances.slice(0, 4).map((b: any) => (
          <Col xs={12} sm={6} key={b.id || b.leave_type}>
            <Card size="small">
              <Statistic
                title={b.leave_type_name || b.leave_name || b.leave_type}
                value={b.available_days ?? b.balance_days ?? 0}
                suffix="days available"
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card title="Leave balances" size="small" style={{ marginBottom: 16 }} loading={ctxLoading}>
        <Table
          rowKey={(r) => r.id || `${r.leave_year}-${r.leave_type}`}
          columns={balanceCols}
          dataSource={balances}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No leave balances yet — ask HR to open balances for your leave types.' }}
        />
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="Apply leave" size="small">
            <Form form={form} layout="vertical">
              <Form.Item name="leave_type" label="Leave type" rules={[{ required: true }]}>
                <Select
                  options={leaveTypeOptions}
                  placeholder="Select leave type"
                  onChange={(v) => setSelectedType(v)}
                />
              </Form.Item>
              {selectedBalance && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`Available for ${selectedBalance.leave_type_name || selectedType}: ${
                    selectedBalance.available_days ?? selectedBalance.balance_days ?? 0
                  } day(s) (pending ${Number(selectedBalance.pending || 0)})`}
                />
              )}
              <Form.Item name="dates" label="Period" rules={[{ required: true, message: 'Select dates' }]}>
                <DatePicker.RangePicker style={{ width: '100%' }} />
              </Form.Item>
              {dates?.[0] && dates?.[1] && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Requested days: {calendarDays(dates[0], dates[1])}
                </Text>
              )}
              <Form.Item name="duty_resume_date" label="Duty resume date (optional)">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="reason" label="Reason / remarks">
                <Input.TextArea rows={3} placeholder="Reason for leave" />
              </Form.Item>
              <Space wrap>
                <Button
                  loading={createMut.isPending}
                  onClick={() => save(false).catch(() => undefined)}
                >
                  Save as draft
                </Button>
                <Button
                  type="primary"
                  loading={createMut.isPending}
                  onClick={() => save(true).catch(() => undefined)}
                >
                  Submit for approval
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Approval authority" size="small" loading={ctxLoading} style={{ marginBottom: 16 }}>
            {manager ? (
              <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
                <Descriptions.Item label="Reporting manager">
                  {manager.full_name}
                  {manager.employee_code ? ` (${manager.employee_code})` : ''}
                  {manager.job_title ? ` · ${manager.job_title}` : ''}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                No reporting manager linked on your employee record.
              </Text>
            )}
            {authority?.configured ? (
              <>
                <Text strong>{authority.workflow_name}</Text>
                <Table
                  style={{ marginTop: 8 }}
                  size="small"
                  pagination={false}
                  rowKey={(r: any) => String(r.step_number)}
                  dataSource={(authority.steps || []) as any[]}
                  columns={[
                    { title: '#', dataIndex: 'step_number', width: 48 },
                    {
                      title: 'Level',
                      dataIndex: 'level_name',
                      render: (v: string, r: any) => v || r.approver_label,
                    },
                    { title: 'Approver', dataIndex: 'approver_detail' },
                  ]}
                />
              </>
            ) : (
              <Alert type="warning" showIcon message={authority?.message || 'Approval workflow not configured'} />
            )}
          </Card>
        </Col>
      </Row>

      <Card title="My applications" size="small">
        <Table
          rowKey="id"
          loading={appsLoading}
          columns={appCols}
          dataSource={apps}
          pagination={{ pageSize: 15 }}
          size="small"
        />
      </Card>
    </WorkspaceLayout>
  );
};

export default EssLeavePage;
